import { API_URL } from '../config';

/**
 * Souscription Web Push côté navigateur (PWA).
 * Nécessite le service worker enregistré (production) et les clés VAPID
 * configurées côté API (sinon `getServerConfig().enabled === false`).
 */

export interface PushServerConfig {
  enabled: boolean;
  public_key: string | null;
}

const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}` };
};

/** Convertit la clé VAPID base64 url-safe au format attendu par PushManager. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const pushService = {
  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  },

  async getServerConfig(): Promise<PushServerConfig> {
    const response = await fetch(`${API_URL}/v1/push/public-key`);
    if (!response.ok) throw new Error('Configuration push indisponible');
    const json = await response.json();
    return json.data as PushServerConfig;
  },

  async getLocalSubscription(): Promise<PushSubscription | null> {
    if (!pushService.isSupported()) return null;
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
  },

  /** L'appareil est-il souscrit (localement ET côté serveur pour ce compte) ? */
  async isSubscribed(): Promise<boolean> {
    try {
      const subscription = await pushService.getLocalSubscription();
      if (!subscription) return false;
      const response = await fetch(
        `${API_URL}/v1/push/subscriptions/status?endpoint=${encodeURIComponent(subscription.endpoint)}`,
        { headers: getAuthHeader() }
      );
      if (!response.ok) return false;
      const json = await response.json();
      return Boolean(json.data?.subscribed);
    } catch {
      return false;
    }
  },

  /** Demande la permission, souscrit l'appareil et l'enregistre côté serveur. */
  async subscribe(): Promise<void> {
    if (!pushService.isSupported()) {
      throw new Error('Notifications non supportées par ce navigateur');
    }

    const config = await pushService.getServerConfig();
    if (!config.enabled || !config.public_key) {
      throw new Error('Notifications push non configurées côté serveur');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Permission de notification refusée');
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key) as BufferSource,
      });
    }

    const raw = subscription.toJSON();
    const response = await fetch(`${API_URL}/v1/push/subscriptions`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: { p256dh: raw.keys?.p256dh, auth: raw.keys?.auth },
        user_agent: navigator.userAgent.slice(0, 500),
      }),
    });
    if (!response.ok) {
      throw new Error("Échec de l'enregistrement de l'appareil");
    }
  },

  /** Désinscrit l'appareil localement et côté serveur. */
  async unsubscribe(): Promise<void> {
    const subscription = await pushService.getLocalSubscription();
    if (!subscription) return;

    try {
      await fetch(`${API_URL}/v1/push/subscriptions`, {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    } finally {
      await subscription.unsubscribe();
    }
  },

  /** Notification de test vers les appareils de l'utilisateur. */
  async sendTest(): Promise<number> {
    const response = await fetch(`${API_URL}/v1/push/test`, {
      method: 'POST',
      headers: getAuthHeader(),
    });
    if (!response.ok) throw new Error("Échec de l'envoi du test");
    const json = await response.json();
    return json.data?.sent ?? 0;
  },
};

export default pushService;
